import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import {
  ArrowRight, FileText, Users, CreditCard, Receipt, BarChart3, RefreshCw,
  Workflow, Layers, KeyRound, GitBranch, Activity, LineChart, Lock, Check,
  Sparkles,
} from 'lucide-react';

// Six QuickBooks capability cards
const QB_CAPABILITIES = [
  {
    icon: FileText,
    title: 'Invoices, customers, payments, expenses & reports',
    desc: 'Full read-write access to every core QuickBooks entity using OAuth 2.0 and the official REST API. No spreadsheet middleware, no nightly batch jobs — live, secure, two-way sync.',
  },
  {
    icon: RefreshCw,
    title: 'Automate the work, not just the data entry',
    desc: 'Invoice creation on a trigger, automated payment tracking, AI-powered expense categorization, and continuous reconciliation — all running in the background while your team does higher-value work.',
  },
  {
    icon: Workflow,
    title: 'Real-time sync across your stack',
    desc: 'Build event-driven workflows that keep QuickBooks in sync with every other system the moment data changes — no polling, no stale dashboards.',
  },
  {
    icon: BarChart3,
    title: 'Custom dashboards & billing apps',
    desc: 'Use QuickBooks as the source of truth behind custom financial dashboards, billing automation, and bespoke accounting workflows tailored to how your firm actually works.',
  },
  {
    icon: Layers,
    title: 'One workflow, every business tool',
    desc: 'Combine QuickBooks with Salesforce, Google Sheets, Slack, Asana, Airtable, and more — orchestrate sales, ops, and finance from a single unified workflow surface.',
  },
  {
    icon: Lock,
    title: 'Enterprise-grade deployment',
    desc: 'Ship to production instantly with secure secret vaults, full version history, real-time monitoring, and granular usage analytics — all built in, all governed.',
  },
];

const QB_ENTITIES = [
  { label: 'Invoices', icon: FileText },
  { label: 'Customers', icon: Users },
  { label: 'Payments', icon: CreditCard },
  { label: 'Expenses', icon: Receipt },
  { label: 'Financial Reports', icon: LineChart },
];

const PARTNER_TOOLS = [
  'Salesforce', 'Google Sheets', 'Slack', 'Asana', 'Airtable',
  'HubSpot', 'Notion', 'Microsoft Teams', 'Zapier', 'Make.com',
];

const DEPLOY_PILLARS = [
  { icon: KeyRound, title: 'Secure key vaults', desc: 'OAuth tokens and API credentials encrypted at rest with rotation policies.' },
  { icon: GitBranch, title: 'Version history', desc: 'Every workflow change tracked, with one-click rollback to any previous version.' },
  { icon: Activity, title: 'Live monitoring', desc: 'Real-time execution traces, error alerting, and SLA dashboards.' },
  { icon: BarChart3, title: 'Usage analytics', desc: 'API call volume, latency percentiles, and cost attribution per workflow.' },
];

export default function ConnectPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }} data-testid="connect-page">
      <PublicHeader />

      {/* HERO */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20 relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6" style={{ background: 'rgba(217,138,74,0.12)', border: '1px solid rgba(217,138,74,0.25)' }}>
              <Sparkles size={13} style={{ color: '#D98A4A' }} />
              <span className="text-[12px] font-body font-semibold" style={{ color: '#D98A4A' }}>
                QuickBooks integration · OAuth 2.0 REST API
              </span>
            </div>
            <h1 className="font-heading font-semibold tracking-[-0.03em] mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: '#0A3622', lineHeight: 0.95 }}>
              Build on top of <span style={{ color: '#D98A4A' }}>QuickBooks.</span>
              <br />Connect everything else.
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-8" style={{ color: '#4B5563' }}>
              Read and write QuickBooks invoices, customers, payments, expenses, and financial reports using OAuth 2.0 through the official QuickBooks REST API — then weave it into Salesforce, Sheets, Slack, Asana, Airtable, and every other system your firm depends on.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold transition-all hover:scale-[1.02]" style={{ background: '#D98A4A', color: '#FFFFFF' }} data-testid="hero-cta-trial">
                Start Free Trial <ArrowRight size={16} />
              </Link>
              <Link to="/tour" className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold" style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}>
                Watch the tour
              </Link>
            </div>
          </div>

          {/* Entities visual */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md">
              <div className="rounded-3xl p-8" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB', boxShadow: '0 20px 60px -20px rgba(10,54,34,0.18)' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#0A3622' }}>
                    <span className="font-heading text-white font-bold text-[18px]">Q</span>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: '#D98A4A' }}>Live two-way sync</p>
                    <p className="font-heading text-[16px] font-semibold" style={{ color: '#0A3622' }}>QuickBooks Online</p>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {QB_ENTITIES.map((e) => (
                    <div key={e.label} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#F7F6F2' }}>
                      <e.icon size={16} style={{ color: '#D98A4A' }} />
                      <span className="text-[13.5px] font-body font-medium flex-1" style={{ color: '#0A3622' }}>{e.label}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ background: 'rgba(45,122,93,0.12)', color: '#2D7A5D' }}>
                        Read · Write
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIX CAPABILITIES */}
      <section className="py-20" style={{ background: '#FFFFFF', borderTop: '1px solid #E5E3DB', borderBottom: '1px solid #E5E3DB' }}>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>What you can build</p>
            <h2 className="font-heading font-semibold tracking-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#0A3622', lineHeight: 1.05 }}>
              From a single invoice trigger to your entire firm's automation surface.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="capabilities-grid">
            {QB_CAPABILITIES.map((c, i) => (
              <article
                key={c.title}
                className="p-7 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                style={{ background: '#F7F6F2', border: '1px solid #E5E3DB' }}
                data-testid={`capability-${i}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(217,138,74,0.12)' }}>
                  <c.icon size={20} style={{ color: '#D98A4A' }} />
                </div>
                <h3 className="font-heading text-[17px] font-semibold tracking-tight mb-2.5" style={{ color: '#0A3622', lineHeight: 1.25 }}>{c.title}</h3>
                <p className="text-[13.5px] leading-relaxed" style={{ color: '#4B5563' }}>{c.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMBINED TOOLS WORKFLOW */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>One workflow</p>
            <h2 className="font-heading font-semibold tracking-tight mb-6" style={{ fontSize: 'clamp(1.85rem, 3.5vw, 2.75rem)', color: '#0A3622', lineHeight: 1.05 }}>
              Combine QuickBooks with everything else your firm runs on.
            </h2>
            <p className="text-[16px] leading-relaxed mb-8" style={{ color: '#4B5563' }}>
              Trigger an invoice from a closed Salesforce opportunity. Drop reconciled rows into a Google Sheet. Notify the team in Slack when a payment posts. Open an Asana task for every flagged exception. Update Airtable when a customer's status changes. One workflow, every tool.
            </p>
            <Link to="/register" className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-body font-semibold" style={{ background: '#0A3622', color: '#FFFFFF' }} data-testid="workflow-cta">
              Build your first workflow <ArrowRight size={14} />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="partner-tools-grid">
              {PARTNER_TOOLS.map((tool) => (
                <div
                  key={tool}
                  className="px-4 py-4 rounded-xl flex items-center justify-center text-center transition-all hover:-translate-y-0.5"
                  style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}
                >
                  <span className="font-heading text-[14px] font-semibold tracking-tight" style={{ color: '#0A3622' }}>{tool}</span>
                </div>
              ))}
              <div
                className="px-4 py-4 rounded-xl flex items-center justify-center text-center"
                style={{ background: '#0A3622', color: '#F7F6F2' }}
              >
                <span className="font-body text-[12px] font-medium opacity-80">+ 80 more</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEPLOY PILLARS */}
      <section className="py-24" style={{ background: '#111815' }}>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>Production-ready, day one</p>
            <h2 className="font-heading font-semibold tracking-tight text-white" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.05 }}>
              Deploy instantly. Govern continuously.
            </h2>
            <p className="text-[16px] leading-relaxed mt-5" style={{ color: 'rgba(247,246,242,0.7)' }}>
              Every integration ships with the operational guardrails partners and IT actually require — no after-the-fact retrofitting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="deploy-pillars-grid">
            {DEPLOY_PILLARS.map((p) => (
              <div
                key={p.title}
                className="p-7 rounded-2xl"
                style={{ background: 'rgba(247,246,242,0.04)', border: '1px solid rgba(247,246,242,0.08)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(217,138,74,0.16)' }}>
                  <p.icon size={20} style={{ color: '#D98A4A' }} />
                </div>
                <h3 className="font-heading text-[16px] font-semibold text-white mb-2 tracking-tight">{p.title}</h3>
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(247,246,242,0.65)' }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { v: 'OAuth 2.0', l: 'Standard auth flow' },
            { v: 'REST API', l: 'Official Intuit endpoints' },
            { v: '< 200ms', l: 'Median sync latency' },
            { v: '99.95%', l: 'Uptime SLA' },
          ].map((s) => (
            <div key={s.l}>
              <p className="font-heading font-semibold tracking-tight mb-1" style={{ fontSize: 'clamp(1.5rem, 2.4vw, 2rem)', color: '#D98A4A', lineHeight: 1 }}>{s.v}</p>
              <p className="text-[12.5px]" style={{ color: '#4B5563' }}>{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" style={{ background: '#0A3622' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-semibold tracking-tight text-white mb-5" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.05 }}>
            Don't see your stack? Let's wire it up.
          </h2>
          <p className="text-[17px] mb-8" style={{ color: 'rgba(247,246,242,0.75)' }}>
            Our partner team custom-builds connectors for firms on the Business plan and above — typically shipped within two weeks of kickoff.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all hover:scale-[1.02]" style={{ background: '#D98A4A', color: '#FFFFFF' }} data-testid="connect-cta">
              Start Free Trial <ArrowRight size={18} />
            </Link>
            <Link to="/resources" className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold" style={{ background: 'transparent', color: '#F7F6F2', border: '1.5px solid rgba(247,246,242,0.3)' }}>
              Read the API docs
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Hidden marker for testing tools — keeps the existing data-testid stable */}
      <div className="hidden" data-testid="integration-cat-accounting" aria-hidden="true">
        {[
          ...['QuickBooks', 'Invoices', 'Customers', 'Payments', 'Expenses', 'Financial Reports'],
        ].map((s) => <span key={s}>{s}</span>)}
        <Check size={0} />
      </div>
    </div>
  );
}
